import { useState } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';

import { Game } from '~/types/game';
import { useGames } from '~/hooks/models/useGame';
import { GAMES } from '~/config/global.config';
import { LoadingPage } from '~/pages/LoadingPage';
import { requestChangeGame, requestStartGame } from '~/services/freePlay';
import { useVoteTimer } from '~/hooks/freePlay';

import styles from './GameSelector.module.css';

export type GameSelectorMode = 'start' | 'replace';

export type GameSelectorProps = {
    mode: GameSelectorMode;
    /** Currently running game (only when mode === 'replace'). */
    currentGameID?: string;
    /** Game IDs allowed for this client. Defaults to global GAMES list. */
    gameIDs?: string[];
    /** Eligible voter count, used to show the quorum hint in 'replace' mode. */
    eligibleCount?: number;
    /** Epoch ms until which the local device cannot initiate a new vote. Only used in 'replace' mode. */
    cooldownUntil?: number;
    /** Override the games list (skips Firestore fetch). Used for debug/storybook. */
    games?: Game[];
    /** Override the submit action (skips the API.emitAction). Used for debug/storybook. */
    onPick?: (gameID: string) => void;
    /** Called after the user confirms a choice. */
    onSubmitted?: () => void;
    /** Called when the user backs out (only in 'replace' mode). */
    onClose?: () => void;
};

/* Manual theme curation — the game model has no genre field yet. Games not listed
   here fall into an "others" row, so adding a game never makes it disappear. `key`
   maps to gamepad.freePlay.selector.category.<key> for the localized row title. */
const CATEGORIES: { key: string; ids: string[] }[] = [
    { key: 'party', ids: ['petit-bac', 'draw-guessr', 'unpop', 'gloofy-pop'] },
    { key: 'quiz', ids: ['unquiz', 'unblind-test', 'geoloc', 'estim'] },
    { key: 'action', ids: ['bomber-kitten', 'polyvroom', 'robrawl', 'eggsit'] },
    { key: 'sport', ids: ['footboared', 'unport'] },
];

export function GameSelector({
    mode,
    currentGameID,
    gameIDs,
    eligibleCount = 0,
    cooldownUntil,
    games: providedGames,
    onPick,
    onSubmitted,
    onClose,
}: GameSelectorProps) {
    const { t } = useTranslation();
    const list = gameIDs && gameIDs.length > 0 ? gameIDs : GAMES;
    const fetched = useGames(providedGames === undefined ? list : []);
    const games = providedGames ?? fetched;
    const [picked, setPicked] = useState<string | undefined>(undefined);
    // Hero trailers that failed to load (404 / bad codec) → fall back to the cover.
    const [failedVideos, setFailedVideos] = useState<Record<string, true>>({});
    const isReplace = mode === 'replace';
    /* Tick once a second while the cooldown is active to refresh the countdown label. */
    const cooldownActive = isReplace && !!cooldownUntil && cooldownUntil > Date.now();
    const cooldownSecondsLeft = useVoteTimer(cooldownActive ? cooldownUntil : null);

    if (!games) {
        return <LoadingPage>{t('gamepad.freePlay.selector.loadingGames')}</LoadingPage>;
    }

    const byId = new Map(games.map((g) => [g.id, g]));
    const isCooldown = cooldownActive && cooldownSecondsLeft > 0;
    // Ignore a stale pick that now points at the running game (a switch happened
    // while this selector was open) — you never propose the game already playing.
    const validPicked = picked && !(isReplace && picked === currentGameID) ? picked : undefined;

    // Group into theme rows; anything uncurated lands in the "others" row.
    const used = new Set(CATEGORIES.flatMap((c) => c.ids));
    const rows: { key: string; title: string; items: Game[] }[] = CATEGORIES.map((c) => ({
        key: c.key,
        title: t(`gamepad.freePlay.selector.category.${c.key}`),
        items: c.ids.map((id) => byId.get(id)).filter((g): g is Game => Boolean(g)),
    })).filter((r) => r.items.length > 0);
    const rest = games.filter((g) => !used.has(g.id)).sort((a, b) => list.indexOf(a.id) - list.indexOf(b.id));
    if (rest.length) {
        rows.push({ key: 'others', title: t('gamepad.freePlay.selector.category.others'), items: rest });
    }

    // Hero always previews a game: the explicit pick, else the first selectable one
    // (in replace mode the running game is excluded — you switch *to* something else).
    const orderedSelectable = rows.flatMap((r) => r.items).filter((g) => !(isReplace && g.id === currentGameID));
    const targetID = validPicked ?? orderedSelectable[0]?.id;
    const heroGame = targetID ? byId.get(targetID) : undefined;

    const submit = () => {
        if (!targetID || isCooldown) return;
        if (onPick) {
            onPick(targetID);
        } else if (isReplace) {
            requestChangeGame(targetID);
        } else {
            requestStartGame(targetID);
        }
        onSubmitted?.();
    };

    const ctaLabel = (() => {
        if (isCooldown) return t('gamepad.freePlay.selector.cooldown', { seconds: cooldownSecondsLeft });
        if (!heroGame) return t('gamepad.freePlay.selector.noGames');
        return isReplace
            ? t('gamepad.freePlay.selector.propose', { game: heroGame.name })
            : t('gamepad.freePlay.selector.start', { game: heroGame.name });
    })();

    // Descriptions come from Firestore in a single language (EN), so they bypass i18n.
    // Prefer a localized override keyed by gameID, falling back to the raw Firestore
    // description when no translation exists for that game.
    const heroDesc = heroGame
        ? t(`gamepad.freePlay.selector.gameDescription.${heroGame.id}`, {
              defaultValue: heroGame.description || '',
          })
        : '';

    const quorumNeeded = Math.floor(eligibleCount / 2) + 1;

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                {isReplace && onClose ? (
                    <button
                        type="button"
                        className={styles.close}
                        onClick={onClose}
                        aria-label={t('gamepad.freePlay.close')}
                    >
                        ✕
                    </button>
                ) : (
                    <span style={{ width: 40 }} />
                )}
                <h2 className={styles.title}>
                    {isReplace ? t('gamepad.freePlay.changeGame') : t('gamepad.freePlay.selector.titleStart')}
                </h2>
                <span style={{ width: 40 }} />
            </div>

            {heroGame && (
                <div className={styles.hero}>
                    {heroGame.video && !failedVideos[heroGame.video] ? (
                        <video
                            key={heroGame.video}
                            className={styles.heroMedia}
                            src={heroGame.video}
                            poster={heroGame.cover || heroGame.icon}
                            autoPlay
                            loop
                            playsInline
                            muted
                            onError={() => setFailedVideos((f) => ({ ...f, [heroGame.video as string]: true }))}
                            ref={(el) => {
                                // React's `muted` prop doesn't always stick (known bug) — force it.
                                // eslint-disable-next-line no-param-reassign
                                if (el) el.muted = true;
                            }}
                        />
                    ) : (
                        <div
                            className={styles.heroMedia}
                            style={{ backgroundImage: `url(${heroGame.cover || heroGame.icon})` }}
                        />
                    )}
                    <div className={styles.heroVeil} />
                    <div className={styles.heroInfo}>
                        <span className={styles.heroBadge}>
                            {heroGame.numberOfPlayers} {t('gamepad.freePlay.players')} · {heroGame.playTime}
                        </span>
                        <div className={styles.heroName}>{heroGame.name}</div>
                        {heroDesc && <p className={styles.heroDesc}>{heroDesc}</p>}
                    </div>
                </div>
            )}

            <div className={styles.rowsWrap}>
                <div className={styles.rows}>
                    {rows.map((row) => (
                        <div
                            key={row.key}
                            className={classnames(styles.row, row.items.length >= 3 && styles.rowOverflow)}
                        >
                            <div className={styles.rowTitle}>{row.title}</div>
                            <div className={styles.rowScroll}>
                                {row.items.map((game) => {
                                    const isCurrent = isReplace && game.id === currentGameID;
                                    const isSelected = !isCurrent && game.id === targetID;
                                    return (
                                        <button
                                            key={game.id}
                                            type="button"
                                            className={classnames(
                                                styles.card,
                                                isSelected && styles.cardSelected,
                                                isCurrent && styles.cardCurrent
                                            )}
                                            onClick={() => {
                                                if (!isCurrent) setPicked(game.id);
                                            }}
                                            disabled={isCurrent}
                                            aria-pressed={isSelected}
                                        >
                                            <div className={styles.cardArt}>
                                                <img src={game.icon || game.cover} alt="" className={styles.cardImg} />
                                                {isCurrent && (
                                                    <span className={styles.cardBadge}>
                                                        {t('gamepad.freePlay.selector.current')}
                                                    </span>
                                                )}
                                                {isSelected && (
                                                    <span className={styles.cardCheck} aria-hidden="true">
                                                        ✓
                                                    </span>
                                                )}
                                            </div>
                                            <div className={styles.cardName}>{game.name}</div>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                    {rows.length === 0 && <div className={styles.empty}>{t('gamepad.freePlay.selector.noGames')}</div>}
                </div>
            </div>

            <div className={styles.footer}>
                {isReplace && eligibleCount > 1 && (
                    <p className={styles.footerInfo}>
                        {t('gamepad.freePlay.selector.voteHint', { needed: quorumNeeded, total: eligibleCount })}
                    </p>
                )}
                <button type="button" className={styles.cta} onClick={submit} disabled={!targetID || isCooldown}>
                    {ctaLabel}
                </button>
            </div>
        </div>
    );
}
